import { codeFromCodeableConcept } from '@seen/core/codeableConcept'
import { invariant } from '@seen/core/invariant'
import { ExtendedObservation } from '@seen/core/models/observation'
import { referenceStringToId } from '@seen/core/references'
import { Button, cn, Separator } from '@seen/ui'
import { IconChevronDown, IconTrash } from '@tabler/icons-react'
import type { DefaultError } from '@tanstack/react-query'
import type { NodeViewProps } from '@tiptap/react'
import type { JSX } from 'react'
import { toast } from 'sonner'
import { ActivityList } from '#src/components/activity/ActivityList.tsx'
import { Combobox } from '#src/components/core/combobox/Combobox.tsx'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogContent } from '#src/components/core/dialog/DialogContent.tsx'
import { DialogTitle } from '#src/components/core/dialog/DialogTitle.tsx'
import { DialogTrigger } from '#src/components/core/dialog/DialogTrigger.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import { ResourceFieldForm } from '#src/components/core/form/ResourceFieldForm.tsx'
import { DatePicker } from '#src/components/input/DatePicker.tsx'
import { ObservationDisplay } from '#src/components/rich-text-editor/ObservationNode/ObservationDisplay.tsx'
import { ObservationActivity } from '#src/components/rich-text-editor/ObservationNode/ObservationDisplayClickable/ObservationDialogContent/ObservationActivity.tsx'
import { ObservationNoteFieldForm } from '#src/components/rich-text-editor/ObservationNode/ObservationNoteFieldForm.tsx'
import { ObservationRichTextEditor } from '#src/components/rich-text-editor/ObservationNode/ObservationRichTextEditor.tsx'
import { ObservationWrapper } from '#src/components/rich-text-editor/ObservationNode/ObservationWrapper.tsx'
import type { ObservationNodeAttributes } from '#src/components/rich-text-editor/ObservationNode/types.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useDeleteConfirmationStore } from '#src/hooks/useDeleteConfirmationStore.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { useRead } from '#src/hooks/useRead.ts'

export function ObservationDisplayClickable(props: NodeViewProps): JSX.Element {
  const attributes = props.node.attrs as ObservationNodeAttributes
  const { reference, scaleType } = attributes
  invariant(reference, 'reference is required')

  const { data: observation } = useRead(ExtendedObservation, reference)

  if (!props.editor.isEditable) {
    return (
      <ObservationDisplay
        observation={observation}
        codingDisplay={attributes.codingDisplay}
        scaleType={scaleType}
        noHover
      />
    )
  }

  return (
    <Dialog>
      <DialogTrigger asChild>
        <ObservationDisplay
          observation={observation}
          codingDisplay={attributes.codingDisplay}
          scaleType={scaleType}
        />
      </DialogTrigger>
      <DialogDisplay>
        <ObservationDialogContent {...props} />
      </DialogDisplay>
    </Dialog>
  )
}

function ObservationDialogContent(props: NodeViewProps): JSX.Element {
  const attributes = props.node.attrs as ObservationNodeAttributes
  const { reference, codingDisplay, scaleType, answerOptions } = attributes
  invariant(reference, 'reference is required')
  invariant(scaleType, 'scaleType is required')

  const mutate = usePatchMutation<ExtendedObservation>().mutateAsync
  const openDeleteConfirmation = useDeleteConfirmationStore(
    (state) => state.open
  )
  const { data: observation } = useRead(ExtendedObservation, reference)

  return (
    <DialogContent className="gap-6">
      <div className="flex items-center justify-between gap-2 pt-1 pb-4">
        <ResourceFieldForm
          instance={observation}
          valueFn={(o) => o.effectiveDateTime}
          patchFn={(o, value) => {
            invariant(value, 'Effective date is required')
            o.effectiveDateTime = value
          }}
          render={({ field }) => (
            <DatePicker
              {...field}
              placeholder="Effective Date"
              className="h-10"
            />
          )}
        />
        {observation && !observation.isEnteredInError && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              openDeleteConfirmation({
                title: `Delete "${codingDisplay ?? 'observation'}"?`,
                description:
                  'This will mark the observation as entered-in-error. It can still be recovered by contacting our team.',
                onConfirm: () => {
                  mutate({
                    instance: observation,
                    mutateFn: (obs) => {
                      obs.markAsEnteredInError()
                    },
                  })
                    .then(() =>
                      toast.success(`${codingDisplay ?? 'Observation'} deleted`)
                    )
                    .catch((error: DefaultError) => {
                      toast.error(
                        `Failed to delete ${codingDisplay ?? 'observation'}: ${error}`
                      )
                    })
                },
              })
            }}
          >
            <IconTrash className="size-4" />
          </Button>
        )}
      </div>
      <DialogTitle>{codingDisplay}</DialogTitle>
      {observation && (
        <div className="flex text-sm">
          {scaleType === 'Qn' && (
            <ObservationDialogNumberInput observation={observation} />
          )}
          {(scaleType === 'Ord' || scaleType === 'Nom') && (
            <ObservationDialogChoiceInput
              observation={observation}
              answerOptions={answerOptions}
              scaleType={scaleType}
            />
          )}
          {scaleType === 'Nar' && (
            <ObservationDialogTextInput observation={observation} />
          )}
        </div>
      )}
      <Separator />
      <div className="flex flex-col gap-4">
        <h5>Activity</h5>
        <ActivityList>
          <ObservationActivity
            codingDisplay={attributes.codingDisplay}
            observationId={referenceStringToId(reference)}
            scaleType={scaleType}
          />
        </ActivityList>
      </div>
    </DialogContent>
  )
}

function ObservationDialogChoiceInput({
  observation,
  answerOptions,
  scaleType,
}: {
  observation: ExtendedObservation
  answerOptions: { code: string; display: string }[]
  scaleType: 'Ord' | 'Nom'
}): JSX.Element {
  const practitioner = useCurrentPractitioner()

  const effectiveAnswerOptions =
    answerOptions.length > 0
      ? answerOptions
      : ExtendedObservation.getDefaultAnswerOptions(
          scaleType,
          observation.display
        )

  const comboboxOptions = effectiveAnswerOptions.map((option) => ({
    value: option.code,
    label: option.display,
    keywords: [option.display],
  }))

  return (
    <ObservationWrapper className="max-w-(--container-prose) pr-px" noHover>
      <span>{observation.display}:&nbsp;</span>
      <ResourceFieldForm
        instance={observation}
        valueFn={(o) => codeFromCodeableConcept(o.valueCodeableConcept)}
        patchFn={(o, v) => {
          const answerOption = answerOptions.find((option) => option.code === v)
          invariant(answerOption, 'answerOption is required')
          o.valueCodeableConcept = {
            coding: [answerOption],
            text: answerOption.display,
          }
          o.performerPractitioner = practitioner.reference
        }}
        render={({ field: { value, onChange, ...rest } }) => (
          <Combobox options={comboboxOptions} onSelect={onChange}>
            <Button
              variant="unstyled"
              size="unstyled"
              className="ml-px flex items-center gap-1 rounded-[calc(var(--radius)-5px)] bg-card p-0 px-1 hover:bg-gray-460 focus-visible:ring-2 focus-visible:ring-offset-2"
              {...rest}
            >
              {answerOptions.find((option) => option.code === value)?.display}
              <IconChevronDown className="size-3" />
            </Button>
          </Combobox>
        )}
      />
      &nbsp;&nbsp;
      <div
        className="mr-0.75" // Accounts for the shift below.
      />
      <ObservationNoteFieldForm
        observation={observation}
        className={cn(
          'grow', // So that the editor can grow to fill the available horizontal space when the content goes onto multiple lines.
          '-ml-0.75' // For visual alignment when the content goes onto multiple lines.
        )}
      />
    </ObservationWrapper>
  )
}

function ObservationDialogNumberInput({
  observation,
}: {
  observation: ExtendedObservation
}): JSX.Element {
  const practitioner = useCurrentPractitioner()
  return (
    <ObservationWrapper className="max-w-(--container-prose) pr-px" noHover>
      <span>{observation.display}:&nbsp;</span>
      <ResourceFieldForm
        instance={observation}
        valueFn={(o) => o.valueQuantity}
        patchFn={(o, v) => {
          o.updateValueQuantityValue(v ?? undefined)
          o.performerPractitioner = practitioner.reference
        }}
        debounceTyping
        render={({ field: { value, onChange, ...fieldRest } }) => (
          <input
            type="number"
            value={value ?? ''}
            className="w-[8ch] rounded-[calc(var(--radius)-5px)] pl-1 placeholder:text-placeholder focus-visible:ring-2 focus-visible:ring-zinc-950 focus-visible:outline-hidden"
            onChange={(e) =>
              onChange(
                Number.isNaN(e.target.valueAsNumber)
                  ? null
                  : e.target.valueAsNumber
              )
            }
            placeholder="Value"
            {...fieldRest}
          />
        )}
      />
      {observation?.valueUnit && (
        <span className="text-foreground-secondary">
          &nbsp;{observation.valueUnit}
        </span>
      )}
      &nbsp;&nbsp;
      <div
        className="mr-0.75" // Accounts for the shift below.
      />
      <ObservationNoteFieldForm
        observation={observation}
        className={cn(
          'grow', // So that the editor can grow to fill the available horizontal space when the content goes onto multiple lines.
          '-ml-0.75' // For visual alignment when the content goes onto multiple lines.
        )}
      />
    </ObservationWrapper>
  )
}

function ObservationDialogTextInput({
  observation,
}: {
  observation: ExtendedObservation
}): JSX.Element {
  const practitioner = useCurrentPractitioner()

  return (
    <ObservationWrapper className="max-w-(--container-prose) pr-px" noHover>
      <span>{observation.display}:&nbsp;</span>
      <div
        className="mr-0.75" // Accounts for the shift below.
      />
      <ResourceFieldForm
        instance={observation}
        valueFn={(o) => o.valueString}
        patchFn={(o, v) => {
          o.valueString = v?.trim() ?? undefined
          o.performerPractitioner = practitioner.reference
        }}
        debounceTyping
        render={({ field }) => (
          <ObservationRichTextEditor placeholder="Add text…" {...field} />
        )}
        className={cn(
          'grow', // So that the editor can grow to fill the available horizontal space when the content goes onto multiple lines.
          '-ml-0.75' // For visual alignment when the content goes onto multiple lines.
        )}
      />
    </ObservationWrapper>
  )
}
